import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const languageRef = useRef(null);
  const location = useLocation();

           const navItems = [
      { path: '/about', label: t('nav.about') },
      { path: '/vision-mission', label: t('nav.vision') },
      { path: '/uav-platform', label: 'UAV' },
      { path: '/rd', label: 'R&D' },
      { path: '/products', label: t('nav.products') },
      { path: '/contact', label: t('nav.contact') },
    ];

  const languages = [
    { code: 'en', label: t('language.en'), flag: '🇺🇸' },
    { code: 'fr', label: t('language.fr'), flag: '🇫🇷' },
  ];

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsLanguageOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageRef.current && !languageRef.current.contains(event.target)) {
        setIsLanguageOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Scroll behavior for navbar hide/show
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Show navbar when scrolling up, hide when scrolling down
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down and not at the top
        setIsVisible(false);
      } else {
        // Scrolling up or at the top
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  return (
    <nav 
      className={`bg-black/95 backdrop-blur-md text-white fixed w-full z-navbar border-b-2 border-military-accent transition-transform duration-300 ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
    >
             <div className="max-w-full mx-auto px-2 lg:px-4">
                  <div className="flex items-center justify-between h-16 md:h-20">
           {/* Left side - Logo */}
           <div className="w-32 md:w-40">
             <Link to="/" className="flex items-center justify-center">
                          <img 
                src="/assets/logo-yellow.png" 
                alt="Centron Aerospace" 
                className="h-16 md:h-20 w-auto"
              />
             </Link>
           </div>

                                 {/* Center - Desktop Navigation */}
           <div className="hidden md:flex items-center space-x-8 lg:space-x-12 flex-1 justify-center">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                                 className={`font-bold text-xs lg:text-sm tracking-widest uppercase hover:text-military-accent px-4 lg:px-6 py-2 transition-all duration-300 relative group ${
                  location.pathname === item.path ? 'text-military-accent' : 'text-military-silver'
                }`}
              >
                {item.label}
                <div className={`absolute bottom-0 left-0 w-full h-0.5 bg-military-accent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ${
                  location.pathname === item.path ? 'scale-x-100' : ''
                }`}></div>
              </Link>
            ))}
          </div>

                     {/* Right side - Language Selector & Mobile Menu Button */}
           <div className="flex items-center space-x-3 md:space-x-4 w-32 md:w-40 justify-end">
            {/* Military Language Selector */}
            <div className="relative" ref={languageRef}>
              <button
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                                 className="bg-black/80 backdrop-blur-md border-2 border-military-accent px-3 md:px-4 py-2 font-black text-xs md:text-sm tracking-widest uppercase hover:bg-military-accent hover:text-black transition-all duration-300 text-military-accent rounded-md shadow-lg hover:shadow-military-accent/25"
              >
                <div className="flex items-center space-x-2">
                  <span>🌐</span>
                  <span>{languages.find(lang => lang.code === i18n.language)?.label || t('language.en')}</span>
                  <svg className={`w-3 h-3 transition-transform duration-300 ${isLanguageOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              <AnimatePresence>
                {isLanguageOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    className="absolute right-0 mt-3 bg-black/95 backdrop-blur-md border-2 border-military-accent rounded-md overflow-hidden shadow-xl"
                  >
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`block w-full text-left px-5 py-3 font-black text-xs tracking-widest uppercase hover:bg-military-accent hover:text-black transition-all duration-300 border-b border-military-accent/20 last:border-b-0 ${
                          i18n.language === lang.code ? 'bg-military-accent text-black' : 'text-military-silver'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <span>{lang.flag}</span>
                          <span>{lang.label}</span>
                        </div>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Military Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden border border-military-accent p-2 hover:bg-military-accent hover:text-black transition-all duration-300"
            >
              <div className="w-5 h-5 flex flex-col justify-center items-center">
                <div className={`w-3 h-0.5 bg-current transition-all duration-300 ${isOpen ? 'rotate-45 translate-y-0.5' : '-translate-y-1'}`}></div>
                <div className={`w-3 h-0.5 bg-current transition-all duration-300 ${isOpen ? 'opacity-0' : 'opacity-100'}`}></div>
                <div className={`w-3 h-0.5 bg-current transition-all duration-300 ${isOpen ? '-rotate-45 -translate-y-0.5' : 'translate-y-1'}`}></div>
              </div>
            </button>
          </div>
        </div>

        {/* Military Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-military-accent bg-military-dark/95 backdrop-blur-md"
            >
              <div className="py-4 space-y-1">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`block px-4 py-3 font-bold text-sm tracking-wider uppercase hover:bg-military-accent hover:text-black transition-all duration-300 ${
                      location.pathname === item.path ? 'bg-military-accent text-black' : 'text-military-silver'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navbar; 